import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Utility function to build API URLs
export const buildAPIUrl = (endpoint = "") => {
  const baseUrl = "https://msr-todos-web-api.herokuapp.com";
  // const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

// Enhanced store with API integration
export const useAPIStore = create(
  devtools((set, get) => ({
    // Loading states
    loading: {
      puppies: false,
      videos: false,
      contacts: false,
      stockPhotos: false,
    },

    // Data
    puppies: [],
    puppiesCache: {
      data: [],
      lastFetched: null,
      isComplete: false,
    },

    // Puppy counts from API
    puppyCounts: {
      total: 0,
      available: 0,
      sold: 0,
    },

    currentPage: 1,
    hasMore: true,
    videos: [],
    contacts: [],
    stats: null,
    stockPhotos: [],

    // Error handling
    error: null,

    // Actions
    setLoading: (key, value) =>
      set((state) => ({
        loading: { ...state.loading, [key]: value },
      })),

    setError: (error) => set({ error }),

    clearError: () => set({ error: null }),

    // Optimized puppy management with caching
    fetchPuppies: async (forceRefresh = false) => {
      const state = get();
      const cacheAge = state.puppiesCache.lastFetched
        ? Date.now() - state.puppiesCache.lastFetched
        : Infinity;
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

      // Return cached data if recent and complete
      if (
        !forceRefresh &&
        cacheAge < CACHE_DURATION &&
        state.puppiesCache.isComplete
      ) {
        set({ puppies: state.puppiesCache.data });
        return state.puppiesCache.data;
      }

      set((state) => ({ loading: { ...state.loading, puppies: true } }));
      try {
        const response = await fetch(buildAPIUrl("/api/puppies/"));
        if (!response.ok) throw new Error("Failed to fetch puppies");

        const data = await response.json();

        // Handle new API response structure with counts
        const puppiesData = data.results || data;
        const counts = {
          total: data.total_puppies || 0,
          available: data.available_count || 0,
          sold: data.sold_count || 0,
        };

        // Update cache and state
        const cacheData = {
          data: puppiesData,
          lastFetched: Date.now(),
          isComplete: true,
        };

        set({
          puppies: puppiesData,
          puppiesCache: cacheData,
          puppyCounts: counts,
          error: null,
        });
        return puppiesData;
      } catch (error) {
        set({ error: error.message });
        throw error;
      } finally {
        set((state) => ({ loading: { ...state.loading, puppies: false } }));
      }
    },

    // Fast initial load - get first 10 puppies quickly
    fetchPuppiesInitial: async () => {
      const state = get();

      // Check if we have cached data that's recent
      const cacheAge = state.puppiesCache.lastFetched
        ? Date.now() - state.puppiesCache.lastFetched
        : Infinity;
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

      if (cacheAge < CACHE_DURATION && state.puppiesCache.data.length > 0) {
        set({ puppies: state.puppiesCache.data.slice(0, 10) });
        return state.puppiesCache.data.slice(0, 10);
      }

      set((state) => ({ loading: { ...state.loading, puppies: true } }));
      try {
        // Fetch all puppies since we now get counts in single request
        const response = await fetch(buildAPIUrl("/api/puppies/"));
        if (!response.ok) throw new Error("Failed to fetch puppies");

        const data = await response.json();
        const allPuppies = data.results || data;
        const initialPuppies = allPuppies.slice(0, 10);

        // Extract counts from response
        const counts = {
          total: data.total_puppies || 0,
          available: data.available_count || 0,
          sold: data.sold_count || 0,
        };

        // Set initial puppies immediately with counts
        set({
          puppies: initialPuppies,
          puppyCounts: counts,
          currentPage: 1,
          hasMore: allPuppies.length > 10,
          error: null,
        });

        // Update cache with all data
        const cacheData = {
          data: allPuppies,
          lastFetched: Date.now(),
          isComplete: true,
        };
        set({ puppiesCache: cacheData });

        return initialPuppies;
      } catch (error) {
        set({ error: error.message });
        throw error;
      } finally {
        set((state) => ({ loading: { ...state.loading, puppies: false } }));
      }
    },

    // Background fetch remaining puppies
    fetchRemainingPuppies: async () => {
      try {
        let allPuppies = [...get().puppies];
        let nextUrl = buildAPIUrl("/api/puppies/?page=2");

        while (nextUrl) {
          const response = await fetch(nextUrl);
          if (!response.ok) break;

          const data = await response.json();
          if (data.results) {
            allPuppies = [...allPuppies, ...data.results];
            nextUrl = data.next;
          } else {
            break;
          }
        }

        // Update cache and state
        const cacheData = {
          data: allPuppies,
          lastFetched: Date.now(),
          isComplete: true,
        };

        set({
          puppies: allPuppies,
          puppiesCache: cacheData,
          hasMore: false,
        });
      } catch (error) {
        // Silent fail for background loading
        console.warn("Background puppy loading failed:", error);
      }
    },

    // Load more puppies for infinite scroll
    loadMorePuppies: async () => {
      const state = get();
      if (!state.hasMore || state.loading.puppies) return;

      set((state) => ({ loading: { ...state.loading, puppies: true } }));
      try {
        const nextPage = state.currentPage + 1;
        const response = await fetch(
          buildAPIUrl(`/api/puppies/?page=${nextPage}`),
        );
        if (!response.ok) throw new Error("Failed to load more puppies");

        const data = await response.json();
        const newPuppies = data.results || [];

        set((state) => ({
          puppies: [...state.puppies, ...newPuppies],
          currentPage: nextPage,
          hasMore: !!data.next,
        }));

        return newPuppies;
      } catch (error) {
        set({ error: error.message });
        throw error;
      } finally {
        set((state) => ({ loading: { ...state.loading, puppies: false } }));
      }
    },

    fetchPuppyByPath: async (profilePath) => {
      try {
        const cleanPath = profilePath.startsWith("/")
          ? profilePath.slice(1)
          : profilePath;
        const response = await fetch(buildAPIUrl(`/api/puppies/${cleanPath}/`));
        if (!response.ok) throw new Error(`Puppy not found ${response.status}`);
        return await response.json();
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    // Admin functions (requires authentication)
    createPuppy: async (puppyData) => {
      try {
        const user = get().user;
        const response = await fetch(buildAPIUrl("/api/admin/puppies/"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${user.token}`,
          },
          body: JSON.stringify(puppyData),
        });

        if (!response.ok) throw new Error("Failed to create puppy");

        const newPuppy = await response.json();
        set((state) => ({ puppies: [...state.puppies, newPuppy] }));
        return newPuppy;
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    updatePuppy: async (puppyId, updates) => {
      try {
        const user = get().user;
        const response = await fetch(
          buildAPIUrl(`/api/admin/puppies/${puppyId}/`),
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${user.token}`,
            },
            body: JSON.stringify(updates),
          },
        );

        if (!response.ok) throw new Error("Failed to update puppy");

        const updatedPuppy = await response.json();
        set((state) => ({
          puppies: state.puppies.map((p) =>
            p.id === puppyId ? updatedPuppy : p,
          ),
        }));
        return updatedPuppy;
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    deletePuppy: async (puppyId) => {
      try {
        const user = get().user;
        const response = await fetch(
          buildAPIUrl(`/api/admin/puppies/${puppyId}/`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Token ${user.token}`,
            },
          },
        );

        if (!response.ok) throw new Error("Failed to delete puppy");

        set((state) => ({
          puppies: state.puppies.filter((p) => p.id !== puppyId),
        }));
        return true;
      } catch (error) {
        set({ error: error.message });
        return false;
      }
    },

    // Stock Photos management
    fetchStockPhotos: async (category = null) => {
      set((state) => ({ loading: { ...state.loading, stockPhotos: true } }));
      try {
        let url = "/api/puppies/stock-photos/";
        if (category) {
          url += `?category=${encodeURIComponent(category)}`;
        }

        const response = await fetch(buildAPIUrl(url));
        if (!response.ok) throw new Error("Failed to fetch stock photos");
        const stockPhotos = await response.json();
        set({ stockPhotos, error: null });
        return stockPhotos;
      } catch (error) {
        set({ error: error.message });
        return [];
      } finally {
        set((state) => ({ loading: { ...state.loading, stockPhotos: false } }));
      }
    },

    fetchStockPhotoById: async (id) => {
      try {
        const response = await fetch(`/api/puppies/stock-photos/${id}/`);
        if (!response.ok) throw new Error("Stock photo not found");
        return await response.json();
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    // Helper functions for stock photos
    getStockPhotosByCategory: (category) => {
      const stockPhotos = get().stockPhotos;
      return stockPhotos.filter((photo) => photo.category === category);
    },

    getHeroImages: () => {
      return get().getStockPhotosByCategory("hero");
    },

    getBackgroundImages: () => {
      return get().getStockPhotosByCategory("background");
    },

    getLogoImages: () => {
      return get().getStockPhotosByCategory("logo");
    },

    // Video management
    fetchVideos: async (category = null, featured = null) => {
      set((state) => ({ loading: { ...state.loading, videos: true } }));
      try {
        let url = "/api/videos/";
        const params = new URLSearchParams();

        if (category) params.append("category", category);
        if (featured !== null) params.append("featured", featured.toString());

        if (params.toString()) url += `?${params.toString()}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch videos");
        const videos = await response.json();
        set({ videos, error: null });
      } catch (error) {
        set({ error: error.message });
      } finally {
        set((state) => ({ loading: { ...state.loading, videos: false } }));
      }
    },

    createVideo: async (videoData) => {
      try {
        const user = get().user;
        const response = await fetch(buildAPIUrl("/api/videos/"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${user.token}`,
          },
          body: JSON.stringify(videoData),
        });

        if (!response.ok) throw new Error("Failed to create video");

        const newVideo = await response.json();
        set((state) => ({ videos: [...state.videos, newVideo] }));
        return newVideo;
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    updateVideo: async (videoId, updates) => {
      try {
        const user = get().user;
        const response = await fetch(`/api/videos/${videoId}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${user.token}`,
          },
          body: JSON.stringify(updates),
        });

        if (!response.ok) throw new Error("Failed to update video");

        const updatedVideo = await response.json();
        set((state) => ({
          videos: state.videos.map((v) =>
            v.id === videoId ? updatedVideo : v,
          ),
        }));
        return updatedVideo;
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    deleteVideo: async (videoId) => {
      try {
        const user = get().user;
        const response = await fetch(`/api/videos/${videoId}/`, {
          method: "DELETE",
          headers: {
            Authorization: `Token ${user.token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to delete video");

        set((state) => ({
          videos: state.videos.filter((v) => v.id !== videoId),
        }));
        return true;
      } catch (error) {
        set({ error: error.message });
        return false;
      }
    },

    // Contact form submissions
    fetchContacts: async (startDate = null, endDate = null) => {
      set((state) => ({ loading: { ...state.loading, contacts: true } }));
      try {
        const user = get().user;
        let url = "/api/admin/contacts/";
        const params = new URLSearchParams();

        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);

        if (params.toString()) url += `?${params.toString()}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Token ${user.token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch contacts");
        const contacts = await response.json();
        set({ contacts, error: null });
      } catch (error) {
        set({ error: error.message });
      } finally {
        set((state) => ({ loading: { ...state.loading, contacts: false } }));
      }
    },

    // Dashboard statistics
    fetchDashboardStats: async () => {
      try {
        const user = get().user;
        const response = await fetch(
          buildAPIUrl("/api/admin/dashboard/stats/"),
          {
            headers: {
              Authorization: `Token ${user.token}`,
            },
          },
        );

        if (!response.ok) throw new Error("Failed to fetch stats");
        const stats = await response.json();
        set({ stats, error: null });
        return stats;
      } catch (error) {
        set({ error: error.message });
        return null;
      }
    },

    // Legacy compatibility - keep existing store structure
    user: {
      username: "todosadmin",
      password: "1234",
      token: "",
    },

    dispatch: (args) => {
      // Handle legacy actions if needed
      const { type, payload } = args;

      switch (type) {
        case "LOGIN":
          set((state) => ({
            user: { ...state.user, token: payload },
          }));
          break;
        default:
          break;
      }
    },
  })),
);
